import React, { useEffect, useState } from 'react';
import { fetchImageObjectURL } from '../services/api';

interface Props {
  /** `/api` 以降のリソースパス（例: 'avatars/xxx.jpg'）。null/未指定なら fallback を表示 */
  resource?: string | null;
  alt?: string;
  imgStyle?: React.CSSProperties;
  /** 画像が無い／読み込み中／取得失敗時に表示する内容（イニシャル円やアイコン等） */
  fallback: React.ReactNode;
}

// URL に ?imgdebug=1 が付いている時だけ、画像取得の失敗理由を画面に表示する（診断用）。
const IMG_DEBUG = typeof window !== 'undefined' && window.location.search.includes('imgdebug');

/**
 * 認証付き画像を Authorization ヘッダ経由で取得して表示するコンポーネント。
 * トークン失効時は自動リフレッシュして再取得するため、`<img src>?token=` 方式のように
 * 「一度失敗すると復活しない」問題が起きない。
 */
export const AuthenticatedImage: React.FC<Props> = ({ resource, alt, imgStyle, fallback }) => {
  const [src, setSrc] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!resource) { setSrc(null); setErr(null); return; }
    let alive = true;
    let objectUrl: string | null = null;
    setSrc(null); setErr(null);
    fetchImageObjectURL(resource)
      .then(url => {
        if (!alive) { URL.revokeObjectURL(url); return; }
        objectUrl = url;
        setSrc(url);
      })
      .catch((e: unknown) => {
        const msg = e instanceof Error ? e.message : String(e);
        if (IMG_DEBUG) console.error('[AuthenticatedImage] failed', resource, msg);
        if (alive) { setSrc(null); setErr(msg); }
      });
    return () => { alive = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [resource]);

  if (src) return <img src={src} alt={alt ?? ''} style={imgStyle} />;

  // 診断モード: 失敗理由をフォールバックに重ねて表示
  if (IMG_DEBUG && err) {
    return (
      <span style={{ position: 'relative', display: 'inline-flex' }}>
        {fallback}
        <span style={{ position: 'absolute', bottom: -2, left: '50%', transform: 'translateX(-50%)', fontSize: 8, lineHeight: 1.2, background: '#dc2626', color: '#fff', padding: '1px 3px', borderRadius: 3, whiteSpace: 'nowrap', maxWidth: 140, overflow: 'hidden' }}>
          {err}
        </span>
      </span>
    );
  }
  return <>{fallback}</>;
};
